import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from 'next/dist/server/api-utils';

import { ToastStatus } from '@/core/components/Toast/Toast';
import useInfiniteQuery from '@/core/hooks/api/useInfiniteQuery';
import useMutation from '@/core/hooks/api/useMutation';
import useQuery from '@/core/hooks/api/useQuery';
import { CONVERSATION_SIZE } from '@/core/lib/constants';
import Api from '@/core/lib/new-architecture/api';
import { useAuthContext } from '@/core/lib/new-architecture/context/auth.context';
import getToken from '@/core/lib/new-architecture/query/token';
import type { InfiniteQuery, Query } from '@/core/lib/new-architecture/query/types.query';
import { useToastContext } from '@/core/lib/toast/toast.context';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import { Conversations_TemplateForm } from '@/core/types/conversation';

interface ConversationParams {
  id?: number;
  from?: number;
  size?: number;
}

const keys = {
  base: ['KEY_TO_PREVENT_COLLISIONS', '/conversation'] as const,
  conversation: (data: ConversationParams) => [...keys.base, data.id, data.size, data.from] as const,
};

type AwaitedConversation = Awaited<ReturnType<typeof Api.query.conversation.get>>;

const useConversation: InfiniteQuery<AwaitedConversation, ConversationParams> = ({ data, token, enabled }) => {
  return useInfiniteQuery({
    queryKey: keys.conversation(data),
    queryFn: async ({ pageParam }) => Api.query.conversation.get({ id: data.id!, from: pageParam as number, size: data.size ?? CONVERSATION_SIZE }, await getToken(token)),
    retry: 0,
    initialPageParam: data.from ?? 0,
    getNextPageParam: lastPage => {
      const lastLength = lastPage.messages.length;

      if (lastLength === 0) return undefined;

      const size = data.size ?? CONVERSATION_SIZE;
      return (data.from ?? 0) + size;
    },
    enabled: enabled && !!data.id,
    refetchInterval: 1000 * 30, // stale time of 30 seconds
    refetchOnMount: 'always',
    initialData: undefined,
  });
};

const useInvalidate = () => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.base });
  return { invalidate };
};

const conversationTemplateKeys = {
  base: ['/message/templates'] as const,
};

const useConversationTemplate: Query<Awaited<ReturnType<typeof Api.query.conversation.getConversationsTemplateListResult>>, void> = ({ token }) =>
  useQuery({
    queryKey: conversationTemplateKeys.base,
    queryFn: async () => Api.query.conversation.getConversationsTemplateListResult(await getToken(token)),
  });

const useConversationTemplateInvalidate = () => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: conversationTemplateKeys.base });
  return { invalidate };
};

const useConversationTemplateMutation = () => {
  const { accessToken } = useAuthContext();
  const { t } = useTranslationContext(['common']);
  const toast = useToastContext();
  const queryClient = useQueryClient();

  return useMutation<Awaited<ReturnType<typeof Api.mutation.conversation.updateConversationsTemplate>>, ApiError, Conversations_TemplateForm>({
    mutationFn: async ({ templates }: Conversations_TemplateForm) => Api.mutation.conversation.updateConversationsTemplate({ templates }, await getToken(accessToken)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: conversationTemplateKeys.base });
      toast?.open(t('components.conversation-template.update-success', { ns: 'common' }), 2000, ToastStatus.SUCCESS);
    },
    onError: () => {
      toast?.open(t('components.conversation-template.update-error', { ns: 'common' }), 2000, ToastStatus.DANGER);
    },
  });
};

export default { useConversation, useInvalidate, useConversationTemplate, useConversationTemplateInvalidate, useConversationTemplateMutation };
